import { Fragment, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";
import STUDIO_CSS from "./studioStyle";
import {
  PALETTE,
  TYPE_META,
  SECTION_ICONS,
  HAS_OPTS,
  SEVERITY_COLORS,
  nid,
  newField,
  modelFromTemplate,
  emptyModel,
  templatePayload,
} from "./schema";
import { createTemplate, updateTemplate } from "../../services/serviceRequests";
import { getAllStaff } from "../../services/staff";
// Pulled from the real form's own module rather than restated here: the preview
// is only worth anything if it offers exactly what staff will see.
import { PRIORITY_LEVELS, SHIFTS } from "../ServiceRequestFields";

/* ── tiny inline icon set — same set as the Notes module's Studio ── */
const IC = {
  rows: '<rect x="3" y="4" width="18" height="7" rx="1.5"/><rect x="3" y="13" width="18" height="7" rx="1.5"/>',
  text: '<path d="M4 7V5h16v2M9 5v14M7 19h4"/>',
  para: '<path d="M17 6H9a3 3 0 0 0 0 6h4M13 6v14M7 6h2"/>',
  num: '<path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18"/>',
  date: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  time: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  drop: '<path d="M6 9l6 6 6-6"/>',
  single: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5" fill="currentColor"/>',
  multi: '<rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8 12l3 3 5-6"/>',
  toggle: '<rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="16" cy="12" r="3" fill="currentColor"/>',
  chips: '<path d="M20.6 8.4 12 17l-8.6-8.6a4 4 0 0 1 5.7-5.7L12 5l2.9-2.3a4 4 0 0 1 5.7 5.7z"/>',
  client: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
  staff: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  facility: '<path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/>',
  notif: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0"/>',
  calc: '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8M8 10h2M14 10h2M8 14h2M14 14h2M8 18h2M14 18h2"/>',
  careplan: '<path d="M9 3h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1V4a1 1 0 0 1 1-1z"/><path d="M12 11.5c-.6-1.2-2.5-1.1-2.5.4 0 1 1.2 1.8 2.5 3 1.3-1.2 2.5-2 2.5-3 0-1.5-1.9-1.6-2.5-.4z"/>',
  grip: '<circle cx="9" cy="6" r="1.4"/><circle cx="15" cy="6" r="1.4"/><circle cx="9" cy="12" r="1.4"/><circle cx="15" cy="12" r="1.4"/><circle cx="9" cy="18" r="1.4"/><circle cx="15" cy="18" r="1.4"/>',
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  trash: '<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  arrowUp: '<path d="M12 19V5M5 12l7-7 7 7"/>',
  arrowDown: '<path d="M12 5v14M19 12l-7 7-7-7"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  tag: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/>',
  doc: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>',
  cross: '<path d="M11 2a2 2 0 0 0-2 2v5H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h5v5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-5h5a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-2-2z"/>',
  shield: '<path d="M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  alert: '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/>',
  link: '<path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>',
  chev: '<path d="M15 18l-6-6 6-6"/>',
  code: '<path d="M16 18l6-6-6-6M8 6l-6 6 6 6"/>',
};
const Svg = ({ n, w = 16, s = 2 }) => (
  <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={s}
    strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: IC[n] || "" }} />
);

const NO_VALUE_OPS = ["is empty", "is filled", "is not empty", "has response", "is complete", "is shown", "is hidden"];
const needsValue = (op) => !NO_VALUE_OPS.includes(op);
const operatorsFor = (trig) => {
  if (!trig) return ["is", "is not", "is filled", "is empty"];
  if (trig.kind === "section") return ["is empty", "has response", "is complete", "is shown", "is hidden"];
  const t = trig.type;
  if (["Client", "Staff", "Facility", "Notifications"].includes(t)) return ["is not empty", "is empty"];
  if (t === "MultipleChoice") return ["includes", "does not include", "is not empty", "is empty"];
  if (t === "Number") return ["is", "is not", "greater than", "less than", "is filled", "is empty"];
  if (["Text", "TextArea"].includes(t)) return ["is", "is not", "contains", "is filled", "is empty"];
  return ["is", "is not", "is filled", "is empty"];
};
// value control for a rule that targets a field with a fixed set of choices
const choicesFor = (f) => (HAS_OPTS(f.type) ? f.options : f.type === "Severity" ? (f.severityOptions || []).map((o) => o.label) : null);

// Request Template Studio — same builder architecture as the Notes module's
// NoteTypeStudio (mutable model ref + force-render, native DnD with a
// dropline indicator, conditional-visibility editor, live schema drawer,
// preview mode). Both a field's `conditional` and a section's own `cond`
// persist (Template.fields[]/.sections[]) and are evaluated at runtime by
// ServiceRequestFields/ServiceRequestNewDrawer the same way this preview
// evaluates them here — see schema.js's fieldsFromSections/sectionsPayload.
const nameOf = (val) => val?.name || [val?.firstName, val?.lastName].filter(Boolean).join(" ") || val?.id || "—";
const initialsOf = (n) => String(n || "").replace(/[^A-Za-z ]/g, "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const AV_COLORS = ["#F59E0B", "#2E90FA", "#12B76A", "#EC4899", "#7A5AF8", "#0BA5EC"];

// viewOnly opens the studio as a read-only preview of the saved template (the
// row's View action): the form renders exactly as staff see it, with the Build
// canvas and every save affordance withheld so nothing can be changed.
const ServiceRequestTemplateBuilder = ({ template, close, viewOnly = false }) => {
  const { allStaff } = useSelector((state) => state.staff);
  const { profile } = useSelector((state) => state.profile);
  const currentUserId = profile?.user?.id || profile?.user?._id;
  // Only for the preview's built-in Issue Category row. Read from whatever the
  // Settings screen already loaded rather than fetching: an empty list just
  // previews as an empty picker, which is what a org with no categories has.
  const issueCategories = useSelector((state) => state.serviceRequests?.issueCategories) || [];
  const previewCategoryNames = issueCategories.filter((c) => c.active).map((c) => c.name);

  // allStaff is only ever populated by the Staff module's own fetch — nothing
  // in Service Requests called it before, so the approver/notify list was
  // silently empty (just the profile-fallback owner) unless the admin had
  // already visited Staff earlier in the session. page must be the literal
  // string "all" (not a page number) to get the full roster back in the
  // allStaff field — matches how Settings → Users fetches the same list.
  useEffect(() => {
    getAllStaff("all", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const admins = useMemo(() => {
    // Same role/platform filter as the Note Type template's own approvers
    // picker (src/pages/Home/NoteType/NewNoteType.jsx) — admins on the Ogiso
    // platform only, excluding inactive/deleted staff records.
    const list = (allStaff || []).filter(
      (s) => s?.active !== false && s?.deleted !== true && s?.role === "admin" && s?.platform === "ogiso"
    );
    if (!currentUserId) return list;
    // Show the current user (owner) first — pull them from the staff list, or fall
    // back to the logged-in profile if they aren't in it.
    const me = list.find((a) => String(a.id || a._id) === String(currentUserId)) || profile?.user || null;
    const rest = list.filter((a) => String(a.id || a._id) !== String(currentUserId));
    return me ? [me, ...rest] : list;
  }, [allStaff, currentUserId, profile]);
  const initial = useMemo(() => (template ? modelFromTemplate(template) : emptyModel()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template]);
  const model = useRef(initial);
  const [, force] = useReducer((x) => x + 1, 0);
  const rerender = force;
  const dragRef = useRef(null);
  const [sel, setSel] = useState({ kind: "note" });
  const [mode, setMode] = useState(viewOnly ? "preview" : "build");
  const [step, setStep] = useState("build");
  const [settingsWizStep, setSettingsWizStep] = useState(0);
  const [newStatusName, setNewStatusName] = useState("");
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);
  const [pv, setPv] = useState({});
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [swatchFor, setSwatchFor] = useState(null); // severity option index currently showing its color popover

  const M = model.current;
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  const allFields = () => M.sections.flatMap((s) => s.fields);
  const findField = (id) => {
    for (const s of M.sections) { const f = s.fields.find((x) => x.id === id); if (f) return { f, s }; }
    return {};
  };
  const isDup = (f) => {
    const k = (f.label || "").trim().toLowerCase();
    if (!k) return false;
    return allFields().filter((x) => (x.label || "").trim().toLowerCase() === k).length > 1;
  };
  const triggersFor = (target) => {
    const out = [];
    if (target.kind === "section") {
      const idx = M.sections.findIndex((s) => s.id === target.id);
      M.sections.slice(0, idx).forEach((s) => {
        out.push({ value: "SEC::" + s.id, label: s.title, kind: "section" });
        s.fields.forEach((f) => out.push({ value: f.label, label: f.label, kind: "field", type: f.type }));
      });
    } else {
      const ord = allFields();
      const i = ord.findIndex((x) => x.id === target.f.id);
      const secIdx = M.sections.findIndex((s) => s.fields.some((x) => x.id === target.f.id));
      M.sections.slice(0, secIdx).forEach((s) => out.push({ value: "SEC::" + s.id, label: s.title, kind: "section" }));
      ord.slice(0, i).forEach((f) => out.push({ value: f.label, label: f.label, kind: "field", type: f.type }));
    }
    return out;
  };

  const addSection = (beforeSecId) => {
    const s = { id: "s" + nid(), title: "New Section", icon: SECTION_ICONS[M.sections.length % SECTION_ICONS.length], cond: null, fields: [] };
    const i = beforeSecId ? M.sections.findIndex((x) => x.id === beforeSecId) : M.sections.length;
    M.sections.splice(i < 0 ? M.sections.length : i, 0, s);
    setSel({ kind: "section", id: s.id }); rerender();
  };
  const moveSectionBefore = (id, beforeSecId) => {
    const from = M.sections.findIndex((s) => s.id === id); if (from < 0) return;
    const [m] = M.sections.splice(from, 1);
    let to = beforeSecId ? M.sections.findIndex((s) => s.id === beforeSecId) : M.sections.length;
    if (to < 0) to = M.sections.length;
    M.sections.splice(to, 0, m); rerender();
  };
  const moveSection = (id, dir) => {
    const i = M.sections.findIndex((s) => s.id === id); const j = i + dir;
    if (j < 0 || j >= M.sections.length) return;
    const [m] = M.sections.splice(i, 1); M.sections.splice(j, 0, m); rerender();
  };
  const duplicateSection = (id) => {
    const i = M.sections.findIndex((s) => s.id === id);
    const copy = JSON.parse(JSON.stringify(M.sections[i]));
    copy.id = "s" + nid(); copy.title += " (copy)"; copy.fields.forEach((f) => { f.id = nid(); });
    M.sections.splice(i + 1, 0, copy); setSel({ kind: "section", id: copy.id }); rerender();
  };
  const deleteSection = (id) => {
    if (M.sections.length <= 1) { flash("A template needs at least one section."); return; }
    model.current.sections = M.sections.filter((x) => x.id !== id);
    if (sel.id === id) setSel({ kind: "note" });
    rerender();
  };
  const addField = (secId, type, beforeId) => {
    const s = M.sections.find((x) => x.id === secId); if (!s) return;
    const nf = newField(type);
    const i = beforeId ? s.fields.findIndex((x) => x.id === beforeId) : -1;
    s.fields.splice(i < 0 ? s.fields.length : i, 0, nf);
    setSel({ kind: "field", id: nf.id }); rerender();
  };
  const moveField = (id, secId, beforeId) => {
    const { f, s } = findField(id); if (!f) return;
    s.fields = s.fields.filter((x) => x.id !== id);
    const dest = M.sections.find((x) => x.id === secId); if (!dest) return;
    const i = beforeId && beforeId !== id ? dest.fields.findIndex((x) => x.id === beforeId) : -1;
    dest.fields.splice(i < 0 ? dest.fields.length : i, 0, f);
    rerender();
  };
  const deleteField = (id) => {
    const { s } = findField(id); if (!s) return;
    s.fields = s.fields.filter((x) => x.id !== id);
    if (sel.id === id) setSel({ kind: "note" });
    rerender();
  };
  const setField = (id, patch) => { const { f } = findField(id); if (f) Object.assign(f, patch); rerender(); };

  /* ── drag & drop (native) with live drop-line indicators ──
     The drop lines and the dragged card's faded state are rendered, not
     poked into the DOM: raw nodes injected into React-owned children fight
     reconciliation the moment anything else re-renders mid-drag (a toast
     expiring, say), which is what made a drop land in the wrong place — or
     not at all. Refs mirror the same values so the drop handler reads the
     target without waiting on a state flush. */
  const dropRef = useRef(null);           // { secId, beforeId } — where a field would land
  const secDropRef = useRef(undefined);   // beforeSecId | null (end) | undefined (no section drag)
  const [fieldDrop, setFieldDrop] = useState(null);
  const [secDrop, setSecDrop] = useState(undefined);
  const [draggingId, setDraggingId] = useState(null);

  const clearFieldDropline = () => { dropRef.current = null; setFieldDrop(null); };
  const clearSectionDropline = () => { secDropRef.current = undefined; setSecDrop(undefined); };
  const clearDroplines = () => { clearFieldDropline(); clearSectionDropline(); };
  const endDrag = () => { dragRef.current = null; setDraggingId(null); clearDroplines(); };

  // The payload rides on the event as well as in the ref, so a drop still
  // resolves if the component re-rendered between dragstart and drop. Firefox
  // also refuses to start a drag at all unless dataTransfer carries something.
  const DRAG_DATA = {
    new: (t) => "new:" + t,
    move: (id) => "move:" + id,
    movesection: (id) => "movesection:" + id,
    newsection: () => "newsection",
  };
  const startDrag = (e, payload, effect) => {
    dragRef.current = payload;
    e.dataTransfer.effectAllowed = effect;
    e.dataTransfer.setData("text/plain", DRAG_DATA[payload.kind](payload.type || payload.id));
    setDraggingId(payload.id || null);
  };
  const dragPayload = (e) => {
    if (dragRef.current) return dragRef.current;
    const raw = (e.dataTransfer && e.dataTransfer.getData("text/plain")) || "";
    if (raw === "newsection") return { kind: "newsection" };
    if (raw.startsWith("new:")) return { kind: "new", type: raw.slice(4) };
    if (raw.startsWith("move:")) return { kind: "move", id: raw.slice(5) };
    if (raw.startsWith("movesection:")) return { kind: "movesection", id: raw.slice(12) };
    return null;
  };

  // Cards sit on a 12-column grid, so "before" is decided by X within a row
  // and by Y between rows — the vertical-only test refused to drop between
  // two half-width fields sitting side by side.
  const placeFieldDropline = (grid, e) => {
    if (!grid) return;
    const dragId = dragRef.current?.kind === "move" ? dragRef.current.id : null;
    let beforeId = null;
    for (const c of grid.querySelectorAll("[data-field]")) {
      const id = c.getAttribute("data-field");
      if (id === dragId) continue;
      const r = c.getBoundingClientRect();
      const inRow = e.clientY >= r.top && e.clientY <= r.bottom;
      if (inRow ? e.clientX < r.left + r.width / 2 : e.clientY < r.top) { beforeId = id; break; }
    }
    const next = { secId: grid.getAttribute("data-grid"), beforeId };
    const cur = dropRef.current;
    dropRef.current = next;
    if (!cur || cur.secId !== next.secId || cur.beforeId !== next.beforeId) setFieldDrop(next);
  };
  const placeSectionDropline = (sheet, e) => {
    const dragId = dragRef.current?.kind === "movesection" ? dragRef.current.id : null;
    let before = null;
    for (const s of sheet.querySelectorAll("[data-section]")) {
      const id = s.getAttribute("data-section");
      if (id === dragId) continue;
      const r = s.getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) { before = id; break; }
    }
    if (secDropRef.current !== before) { secDropRef.current = before; setSecDrop(before); }
  };

  const onFieldDrop = (secId, e) => {
    e.preventDefault(); e.stopPropagation();
    const d = dragPayload(e);
    if (!d || (d.kind !== "new" && d.kind !== "move")) { endDrag(); return; }
    const beforeId = dropRef.current && dropRef.current.secId === secId ? dropRef.current.beforeId : null;
    if (d.kind === "new") addField(secId, d.type, beforeId);
    else moveField(d.id, secId, beforeId);
    endDrag();
  };
  const onSheetDrop = (e) => {
    const d = dragPayload(e);
    if (!d || (d.kind !== "newsection" && d.kind !== "movesection")) return;
    e.preventDefault();
    const beforeSecId = secDropRef.current === undefined ? null : secDropRef.current;
    if (d.kind === "newsection") addSection(beforeSecId);
    else moveSectionBefore(d.id, beforeSecId);
    endDrag();
  };

  /* ── validation + save ── */
  const validate = () => {
    const issues = [];
    if (!(M.name || "").trim()) issues.push("The request template needs a name.");
    allFields().forEach((f) => { if (!(f.label || "").trim()) issues.push("A field has no name."); });
    const counts = {};
    allFields().forEach((f) => { const k = (f.label || "").trim().toLowerCase(); if (k) counts[k] = (counts[k] || 0) + 1; });
    Object.entries(counts).forEach(([k, c]) => { if (c > 1) issues.push(`${c} fields share the name "${k}". Names must be unique.`); });
    M.sections.forEach((s) => { if (!s.fields.length) issues.push(`Section "${s.title}" has no fields.`); });
    return issues;
  };
  const goSettings = () => {
    const issues = validate();
    if (issues.length) { flash(issues[0]); return; }
    setSettingsWizStep(0);
    setStep("settings");
  };
  const save = async () => {
    const issues = validate();
    if (issues.length) { flash(issues[0]); return; }
    setSaving(true);
    const payload = templatePayload(M);
    const done = () => { setSaving(false); close(); };
    if (M.id) updateTemplate(M.id, payload, done);
    else createTemplate(payload, done);
  };
  const back = () => { if (step === "settings") setStep("build"); else close(); };

  const escHtml = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const jsonHi = (o) => escHtml(JSON.stringify(o, null, 2))
    .replace(/&quot;([^&]+?)&quot;:/g, '<span class="k">"$1"</span>:')
    .replace(/: &quot;([^&]*?)&quot;/g, ': <span class="s">"$1"</span>')
    .replace(/: (\d+|true|false)/g, ': <span class="n">$1</span>');

  /* ───────────────────────── render pieces ───────────────────────── */
  const secIcon = (icon, w = 14) =>
    icon && icon.indexOf("img:") === 0
      ? <img src={icon.slice(4)} alt="" style={{ width: w, height: w, objectFit: "cover", borderRadius: 3 }} />
      : <Svg n={icon || "tag"} w={w} />;

  // Where a tapped palette chip lands. Dragging aims at a spot; a tap has no
  // aim, so it appends to whatever the author is currently working in — the
  // selected section, the section owning the selected field, or the last one.
  const addFieldByTap = (type) => {
    if (type === "section") { addSection(sel?.kind === "section" ? sel.id : null); return; }
    if (!M.sections.length) { flash("Add a section first."); return; }
    const target = sel?.kind === "section"
      ? M.sections.find((s) => s.id === sel.id)
      : sel?.kind === "field"
        ? findField(sel.id).s
        : null;
    addField((target || M.sections[M.sections.length - 1]).id, type, null);
  };

  const renderPalette = () => (
    <div className="pane rail">
      {PALETTE.map((g) => (
        <div key={g.group}>
          <h3>{g.group}</h3>
          <div className="palette">
            {g.items.map((it) => (
              // Tapping is the touch equivalent of the drag: HTML5 drag events
              // never fire from a finger, so without this the whole palette is
              // inert on a phone or tablet.
              <div key={it.type} className={`pchip ${g.cls}`} draggable
                role="button"
                tabIndex={0}
                title={`Add ${it.name}`}
                onDragStart={(e) => startDrag(e, it.type === "section" ? { kind: "newsection" } : { kind: "new", type: it.type }, "copy")}
                onDragEnd={endDrag}
                onClick={() => addFieldByTap(it.type)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); addFieldByTap(it.type); } }}>
                <span className="ic"><Svg n={it.ic} w={15} /></span>
                {it.name}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="hinttip"><b>Tap</b> a field to add it to the section you&apos;re in, or <b>drag</b> it onto the canvas to place it exactly. Drag cards to reorder. Select a field to edit its label, width, options, conditional rule or formula.</div>
    </div>
  );

  const fieldPreviewLine = (f) => {
    if (f.type === "Calculation") return "ƒ " + (f.calc?.formula || "—");
    if (f.type === "IssueCategory") return "From Settings → Issue Categories";
    if (HAS_OPTS(f.type)) return (f.options || []).slice(0, 3).join(" · ") + ((f.options || []).length > 3 ? " …" : "");
    if (f.type === "Severity") return (f.severityOptions || []).map((o) => o.label).join(" · ");
    if (f.type === "YesNo") return `${f.noLabel || "No"} / ${f.yesLabel || "Yes"}`;
    if (f.type === "CarePlan") return "Reveals the client's active care plan";
    if (f.help) return f.help;
    return TYPE_META[f.type]?.label || "";
  };

  const renderCanvas = () => (
    <div className="pane canvas">
      <div className="sheet" data-sheet
        onDragOver={(e) => {
          const k = dragPayload(e)?.kind;
          if (k !== "newsection" && k !== "movesection") return;
          e.preventDefault();
          e.dataTransfer.dropEffect = k === "newsection" ? "copy" : "move";
          placeSectionDropline(e.currentTarget, e);
        }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) clearSectionDropline(); }}
        onDrop={onSheetDrop}>
        <div className={`ntheader ${sel?.kind === "note" ? "selected" : ""}`} onClick={() => setSel({ kind: "note" })}>
          {/* A plain heading here read as clickable-to-rename (the same
              focus ring a genuinely editable title gets elsewhere on the
              canvas, e.g. a section's own .stitle) without being one —
              typing did nothing, and the only real way to set it was the
              Title field in the settings panel. A real input bound to the
              same M.name keeps both in sync either way. */}
          <input
            className="tt"
            data-stop
            value={M.name}
            onChange={(e) => { M.name = e.target.value; rerender(); }}
            placeholder="Untitled request template"
          />
          {M.desc ? <div className="ds">{M.desc}</div> : null}
        </div>

        {M.sections.map((sec) => {
          const selS = sel?.kind === "section" && sel.id === sec.id;
          const hasCond = sec.cond && sec.cond.rules.length;
          const dropHere = fieldDrop && fieldDrop.secId === sec.id;
          return (
            <Fragment key={sec.id}>
            {secDrop === sec.id ? <div className="secdropline" /> : null}
            <div className={`section ${selS ? "selected" : ""} ${draggingId === sec.id ? "dragging" : ""}`} data-section={sec.id}
              onClick={(e) => { if (e.target.closest("[data-field]") || e.target.closest("[data-stop]")) return; setSel({ kind: "section", id: sec.id }); }}
              /* the whole card takes a field drop, not just its grid — the
                 header and padding are part of the target the author aims at */
              onDragOver={(e) => {
                const k = dragPayload(e)?.kind;
                if (k !== "new" && k !== "move") return;   // section drags belong to the sheet
                e.preventDefault(); e.stopPropagation();
                e.dataTransfer.dropEffect = k === "new" ? "copy" : "move";
                placeFieldDropline(e.currentTarget.querySelector("[data-grid]"), e);
              }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) clearFieldDropline(); }}
              /* a section dropped on another section is a reorder — leave it
                 to bubble up to the sheet rather than swallowing it here */
              onDrop={(e) => { const k = dragPayload(e)?.kind; if (k === "new" || k === "move") onFieldDrop(sec.id, e); }}>
              <div className="shead">
                <span className="grip" draggable data-stop
                  onDragStart={(e) => startDrag(e, { kind: "movesection", id: sec.id }, "move")}
                  onDragEnd={endDrag}><Svg n="grip" w={16} /></span>
                <span className="sico">{secIcon(sec.icon)}</span>
                <input className="stitle" data-stop value={sec.title}
                  onChange={(e) => { sec.title = e.target.value; rerender(); }} />
                <button className={`sfx ${hasCond ? "on" : ""}`} data-stop onClick={() => openSectionCond(sec)}>
                  <Svg n="eye" w={13} /> {hasCond ? "Conditional" : "Add condition"}
                </button>
                <button className="sdel" data-stop title="Delete section" onClick={() => deleteSection(sec.id)}><Svg n="trash" w={15} /></button>
              </div>
              <div className={`fgrid ${dropHere ? "dragover" : ""}`} data-grid={sec.id}>
                {!sec.fields.length && !dropHere ? <div className="empty">Drop fields here</div> : null}
                {sec.fields.map((f) => {
                  const selF = sel?.kind === "field" && sel.id === f.id;
                  return (
                    <Fragment key={f.id}>
                    {dropHere && fieldDrop.beforeId === f.id ? <div className="dropline" /> : null}
                    <div className={`fcard ${selF ? "selected" : ""} ${isDup(f) ? "dup" : ""} ${draggingId === f.id ? "dragging" : ""}`}
                      style={{ gridColumn: `span ${f.span}` }} data-field={f.id} draggable
                      onDragStart={(e) => { e.stopPropagation(); startDrag(e, { kind: "move", id: f.id }, "move"); }}
                      onDragEnd={endDrag}
                      onClick={(e) => { if (e.target.closest("[data-stop]")) return; setSel({ kind: "field", id: f.id }); }}>
                      <div className="frow">
                        <span className="fgrip"><Svg n="grip" w={15} /></span>
                        <span className="flabel">{f.label || "Untitled"}{f.required ? <span className="freq"> *</span> : null}</span>
                        <span className="fbadges">
                          {isDup(f) ? <span className="fbtn" title="Duplicate name"><Svg n="alert" w={13} /></span> : null}
                          <span className="ftype">{TYPE_META[f.type]?.label || f.type}</span>
                          <button className={`fbtn ${f.conditional?.rules?.length ? "on" : ""}`} data-stop title="Conditional visibility" onClick={() => openFieldCond(f)}><Svg n="eye" w={14} /></button>
                          <button className="fbtn del" data-stop title="Delete" onClick={() => deleteField(f.id)}><Svg n="trash" w={14} /></button>
                        </span>
                      </div>
                      <div className="fprev">{fieldPreviewLine(f)}</div>
                    </div>
                    </Fragment>
                  );
                })}
                {dropHere && !fieldDrop.beforeId ? <div className="dropline" /> : null}
              </div>
            </div>
            </Fragment>
          );
        })}
        {secDrop === null ? <div className="secdropline" /> : null}
        <button className="addsection" onClick={() => addSection()}><Svg n="plus" w={15} /> Add section</button>
      </div>
    </div>
  );

  const openFieldCond = (f) => {
    const trig = triggersFor({ kind: "field", f });
    setSel({ kind: "field", id: f.id });
    if (!trig.length) { rerender(); flash("This is the first field — nothing earlier to gate it on."); return; }
    f.conditional = f.conditional || { mode: "all", rules: [] };
    if (!f.conditional.rules.length) { const t = trig[0]; f.conditional.rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); }
    rerender();
  };
  const openSectionCond = (sec) => {
    const trig = triggersFor({ kind: "section", id: sec.id });
    setSel({ kind: "section", id: sec.id });
    if (!trig.length) { rerender(); flash("A section can only be gated by a field or section above it."); return; }
    sec.cond = sec.cond || { mode: "all", rules: [] };
    if (!sec.cond.rules.length) { const t = trig[0]; sec.cond.rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); }
    rerender();
  };

  const conditionalEditor = ({ owner, keyName, target }) => {
    const trig = triggersFor(target);
    const c = owner[keyName];
    const noun = target.kind === "section" ? "section" : "field";
    if (!trig.length) return (<div className="psec"><div className="ph"><Svg n="eye" w={13} /> Conditional visibility</div><div className="note">Nothing earlier to depend on yet.</div></div>);
    const addRule = () => { owner[keyName] = c || { mode: "all", rules: [] }; const t = trig[0]; owner[keyName].rules.push({ kind: t.kind, field: t.value, op: operatorsFor(t)[0], value: "" }); rerender(); };
    return (
      <div className="psec">
        <div className="ph"><Svg n="eye" w={13} /> Conditional visibility</div>
        {!c || !c.rules.length ? (
          <>
            <div className="note" style={{ marginBottom: 8 }}>Always visible.</div>
            <button className="ghost" style={{ width: "100%" }} onClick={addRule}><Svg n="plus" w={13} /> Add a show-when rule</button>
          </>
        ) : (
          <>
            <div className="note" style={{ marginBottom: 6 }}>Show this {noun} when{" "}
              <span className="modepick">
                <button aria-pressed={c.mode === "all"} onClick={() => { c.mode = "all"; rerender(); }}>all</button>
                <button aria-pressed={c.mode === "any"} onClick={() => { c.mode = "any"; rerender(); }}>any</button>
              </span>{" "}match:</div>
            {c.rules.map((r, i) => {
              const t = trig.find((x) => x.value === r.field) || null;
              const ops = operatorsFor(t);
              const valField = t && t.kind === "field" ? allFields().find((x) => x.label === t.value) : null;
              const valChoices = valField ? choicesFor(valField) : null;
              return (
                <div className="rule" key={i}>
                  <div className="rr">
                    <select className="inp" value={r.field} style={{ flex: 1 }}
                      onChange={(e) => { r.field = e.target.value; const nt = trig.find((x) => x.value === r.field); r.kind = nt ? nt.kind : r.kind; r.op = operatorsFor(nt)[0]; r.value = ""; rerender(); }}>
                      {trig.map((tt) => <option key={tt.value} value={tt.value}>{tt.kind === "section" ? "Section: " : ""}{tt.label}</option>)}
                    </select>
                    <select className="inp" value={r.op} style={{ flex: 1 }}
                      onChange={(e) => { r.op = e.target.value; if (!needsValue(r.op)) r.value = ""; rerender(); }}>
                      {ops.map((op) => <option key={op} value={op}>{op}</option>)}
                    </select>
                  </div>
                  {needsValue(r.op) ? (
                    valChoices ? (
                      <select className="inp" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }}>
                        <option value="">Choose a value…</option>
                        {valChoices.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : valField && valField.type === "YesNo" ? (
                      <select className="inp" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }}>
                        <option value="">Choose…</option><option value="Yes">Yes</option><option value="No">No</option>
                      </select>
                    ) : (
                      <input className="inp" placeholder="value" value={r.value} onChange={(e) => { r.value = e.target.value; rerender(); }} />
                    )
                  ) : null}
                  <button className="del" onClick={() => { c.rules.splice(i, 1); if (!c.rules.length) owner[keyName] = null; rerender(); }}>Remove</button>
                </div>
              );
            })}
            <button className="ghost" style={{ width: "100%" }} onClick={addRule}><Svg n="plus" w={13} /> Add rule</button>
          </>
        )}
      </div>
    );
  };

  const renderProps = () => {
    if (!sel || sel.kind === "note") {
      return (
        <div className="pane props">
          <div className="phead"><div className="pt">Request template</div><div className="ps">Title & description</div></div>
          <div className="pbody">
            <div className="field"><label>Title</label>
              <input className="inp" value={M.name} placeholder="Template title" onChange={(e) => { M.name = e.target.value; rerender(); }} /></div>
            <div className="field"><label>Description <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
              <textarea className="inp" rows={4} value={M.desc} placeholder="What is this request template for?" onChange={(e) => { M.desc = e.target.value; rerender(); }} /></div>
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)", fontSize: 12.5, color: "var(--muted)" }}>
              Select a field or section on the canvas to edit its properties here.
            </div>
          </div>
        </div>
      );
    }
    if (sel.kind === "section") {
      const sec = M.sections.find((s) => s.id === sel.id);
      if (!sec) return <div className="pane props" />;
      const idx = M.sections.findIndex((s) => s.id === sec.id);
      const isImg = sec.icon && sec.icon.indexOf("img:") === 0;
      return (
        <div className="pane props">
          <div className="phead"><div className="pt">Section</div><div className="ps">Card settings</div></div>
          <div className="pbody">
            <div className="spanpick" style={{ marginBottom: 14 }}>
              <button disabled={idx === 0} onClick={() => moveSection(sec.id, -1)}><Svg n="arrowUp" w={14} /></button>
              <button disabled={idx === M.sections.length - 1} onClick={() => moveSection(sec.id, 1)}><Svg n="arrowDown" w={14} /></button>
              <button onClick={() => duplicateSection(sec.id)}><Svg n="copy" w={14} /></button>
              <button onClick={() => deleteSection(sec.id)} style={{ color: "var(--danger)" }}><Svg n="trash" w={14} /></button>
            </div>
            <div className="field"><label>Title</label>
              <input className="inp" value={sec.title} onChange={(e) => { sec.title = e.target.value; rerender(); }} /></div>
            <div className="field"><label>Icon</label>
              <div className="iconpick">
                {SECTION_ICONS.map((ic) => (
                  <button key={ic} aria-pressed={!isImg && sec.icon === ic} onClick={() => { sec.icon = ic; rerender(); }}><Svg n={ic} w={15} /></button>
                ))}
              </div>
            </div>
            {conditionalEditor({ owner: sec, keyName: "cond", target: { kind: "section", id: sec.id } })}
          </div>
        </div>
      );
    }
    // field
    const { f } = findField(sel.id);
    if (!f) return <div className="pane props" />;
    const numericOthers = allFields().filter((x) => x.id !== f.id && ["Number", "Calculation"].includes(x.type));
    return (
      <div className="pane props">
        <div className="phead"><div className="pt">{TYPE_META[f.type]?.label || f.type}</div><div className="ps">Field settings</div></div>
        <div className="pbody">
          <div className="field"><label>Label</label>
            <input className="inp" value={f.label} onChange={(e) => setField(f.id, { label: e.target.value })} /></div>
          {isDup(f) ? <div className="calcout" style={{ color: "var(--danger)" }}><Svg n="alert" w={12} /> Another field uses this name. Names must be unique for conditions and calculations.</div> : null}
          {["Text", "TextArea", "Number", "Date", "Time", "Dropdown"].includes(f.type) ? (
            <div className="field"><label>Placeholder</label>
              <input className="inp" value={f.placeholder || ""} onChange={(e) => setField(f.id, { placeholder: e.target.value })} /></div>
          ) : null}
          <div className="field"><label>Help text</label>
            <input className="inp" value={f.help || ""} onChange={(e) => setField(f.id, { help: e.target.value })} /></div>
          <div className="field"><label>Width</label>
            <div className="spanpick">
              {[[4, "⅓"], [6, "½"], [12, "Full"]].map(([w, lbl]) => (
                <button key={w} aria-pressed={f.span === w} onClick={() => setField(f.id, { span: w })}>{lbl}</button>
              ))}
            </div>
          </div>
          {f.type !== "Notifications" ? (
            <div className="switch"><label>Required</label>
              <input type="checkbox" className="tog" checked={!!f.required} onChange={(e) => setField(f.id, { required: e.target.checked })} /></div>
          ) : null}

          {HAS_OPTS(f.type) ? (
            <div className="psec"><div className="ph"><Svg n="drop" w={13} /> Options</div>
              <div className="opts">
                {f.options.map((o, i) => (
                  <div className="optrow" key={i}>
                    <input className="inp" value={o} onChange={(e) => { f.options[i] = e.target.value; rerender(); }} />
                    <button onClick={() => { f.options.splice(i, 1); rerender(); }}><Svg n="trash" w={13} /></button>
                  </div>
                ))}
              </div>
              <button className="addopt" onClick={() => { f.options.push("Option " + (f.options.length + 1)); rerender(); }}><Svg n="plus" w={13} /> Add option</button>
            </div>
          ) : null}

          {f.type === "YesNo" ? (
            <div className="psec"><div className="ph"><Svg n="toggle" w={13} /> Labels</div>
              <div className="field"><label>Yes label</label><input className="inp" value={f.yesLabel || ""} onChange={(e) => setField(f.id, { yesLabel: e.target.value })} /></div>
              <div className="field" style={{ marginBottom: 0 }}><label>No label</label><input className="inp" value={f.noLabel || ""} onChange={(e) => setField(f.id, { noLabel: e.target.value })} /></div>
            </div>
          ) : null}

          {f.type === "Severity" ? (
            <div className="psec"><div className="ph"><Svg n="chips" w={13} /> Severity levels</div>
              <div className="opts">
                {(f.severityOptions || []).map((o, i) => (
                  <div key={i}>
                    <div className="optrow">
                      <button className="swatchbtn" style={{ background: o.color }} title="Choose color"
                        onClick={() => setSwatchFor(swatchFor === i ? null : i)} />
                      <input className="inp" value={o.label} onChange={(e) => { f.severityOptions[i] = { ...o, label: e.target.value }; rerender(); }} />
                      <button onClick={() => { f.severityOptions.splice(i, 1); if (swatchFor === i) setSwatchFor(null); rerender(); }} disabled={(f.severityOptions || []).length <= 1}><Svg n="trash" w={13} /></button>
                    </div>
                    {swatchFor === i ? (
                      <div className="swatchpop">
                        {SEVERITY_COLORS.map((c) => (
                          <button key={c} aria-pressed={o.color === c} style={{ background: c }} onClick={() => { f.severityOptions[i] = { ...o, color: c }; setSwatchFor(null); rerender(); }} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <button className="addopt" onClick={() => { f.severityOptions = [...(f.severityOptions || []), { label: "New level", color: SEVERITY_COLORS[0] }]; rerender(); }}><Svg n="plus" w={13} /> Add level</button>
            </div>
          ) : null}

          {/* No "Allow multiple" switch for Client/Staff: both always accept
              several, same as the built-in pickers on the default request
              types (see DirectoryField in ServiceRequestFields). */}

          {f.type === "IssueCategory" ? (
            <div className="switch"><label>Active categories only</label>
              <input type="checkbox" className="tog" checked={!!f.activeOnly} onChange={(e) => setField(f.id, { activeOnly: e.target.checked })} /></div>
          ) : null}

          {f.type === "Calculation" ? (
            <div className="psec"><div className="ph">ƒ Calculation</div>
              <div className="field"><label>Formula</label>
                <textarea className="inp" placeholder="{Field A} + {Field B}" value={f.calc?.formula || ""}
                  onChange={(e) => { f.calc = { ...(f.calc || {}), formula: e.target.value }; rerender(); }} /></div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "-8px 0 8px" }}>Reference numeric fields with {"{curly braces}"}. Use + − × ÷ ( ).</div>
              <div className="field"><label>Insert field</label>
                <select className="inp" value="" onChange={(e) => { if (!e.target.value) return; f.calc = f.calc || { formula: "" }; f.calc.formula = (f.calc.formula ? f.calc.formula + " " : "") + "{" + e.target.value + "}"; rerender(); }}>
                  <option value="">{numericOthers.length ? "Choose a numeric field…" : "No numeric fields yet"}</option>
                  {numericOthers.map((x) => <option key={x.id} value={x.label}>{x.label}</option>)}
                </select></div>
              <div className="field" style={{ marginBottom: 0 }}><label>Decimal places</label>
                <select className="inp" value={String(f.calc?.decimalPlaces ?? 0)} onChange={(e) => { f.calc = { ...(f.calc || {}), decimalPlaces: Number(e.target.value) }; rerender(); }}>
                  {["0", "1", "2", "3", "4"].map((n) => <option key={n} value={n}>{n}</option>)}
                </select></div>
            </div>
          ) : null}

          {f.type === "CarePlan" ? (
            <div className="psec"><div className="ph"><Svg n="careplan" w={13} /> Care Plan</div>
              <div className="field" style={{ marginBottom: 0 }}><label>Scope</label>
                <select className="inp" value={f.carePlanScope || "clientActive"} onChange={(e) => setField(f.id, { carePlanScope: e.target.value })}>
                  <option value="clientActive">Client's active plan</option>
                  <option value="clientAll">All of client's plans</option>
                </select></div>
            </div>
          ) : null}

          {f.type === "Notifications" ? (
            <div className="psec"><div className="ph"><Svg n="notif" w={13} /> Notify roles</div>
              {["admin", "staff", "contractor"].map((role) => {
                const on = (f.notifyRoles || []).includes(role);
                return (
                  <div className="switch" key={role} style={{ textTransform: "capitalize" }}>
                    <label>{role}</label>
                    <input type="checkbox" className="tog" checked={on} onChange={() => {
                      const next = on ? f.notifyRoles.filter((r) => r !== role) : [...(f.notifyRoles || []), role];
                      setField(f.id, { notifyRoles: next });
                    }} />
                  </div>
                );
              })}
            </div>
          ) : null}

          {conditionalEditor({ owner: f, keyName: "conditional", target: { kind: "field", f } })}
        </div>
      </div>
    );
  };

  /* ── preview ── */
  const isFilled = (v) => !(v == null || v === "" || (Array.isArray(v) && !v.length));
  const fieldHolds = (label, op, value) => {
    const v = pv[label];
    switch (op) {
      case "is": return v === value;
      case "is not": return v !== value;
      case "greater than": return Number(v) > Number(value);
      case "less than": return Number(v) < Number(value);
      case "contains": return String(v || "").toLowerCase().includes(String(value || "").toLowerCase());
      case "includes": return Array.isArray(v) && v.includes(value);
      case "does not include": return !(Array.isArray(v) && v.includes(value));
      case "is empty": return !isFilled(v);
      case "is filled": case "is not empty": return isFilled(v);
      default: return true;
    }
  };
  const sectionCondMet = (sec) => condOn(sec.cond);
  const ruleHolds = (r) => {
    if (String(r.field).startsWith("SEC::")) {
      const sec = M.sections.find((s) => s.id === r.field.slice(5)); if (!sec) return true;
      const vals = sec.fields.map((f) => pv[f.label]);
      switch (r.op) {
        case "is empty": return !vals.some(isFilled);
        case "has response": return vals.some(isFilled);
        case "is complete": return sec.fields.filter((f) => f.required).every((f) => isFilled(pv[f.label]));
        case "is shown": return sectionCondMet(sec);
        case "is hidden": return !sectionCondMet(sec);
        default: return true;
      }
    }
    return fieldHolds(r.field, r.op, r.value);
  };
  function condOn(c) { if (!c || !c.rules.length) return true; return c.mode === "any" ? c.rules.some(ruleHolds) : c.rules.every(ruleHolds); }
  const evalCalc = (f) => {
    let expr = (f.calc?.formula || "").replace(/\{([^}]+)\}/g, (m, r) => { const v = Number(pv[r.trim()]); return isNaN(v) ? 0 : v; });
    expr = expr.replace(/×/g, "*").replace(/÷/g, "/");
    if (!expr.trim() || !/^[0-9.+\-*/() ]+$/.test(expr)) return "—";
    try {
      // Input is regex-restricted to digits, operators and parens — safe to evaluate.
      // eslint-disable-next-line no-new-func
      const n = Function('"use strict";return(' + expr + ")")();
      const d = f.calc?.decimalPlaces ?? 2;
      return Number(n.toFixed(d));
    } catch { return "—"; }
  };
  const setPvVal = (label, v) => setPv((p) => ({ ...p, [label]: v }));
  const pvField = (f) => {
    const lab = <label>{f.label}{f.required ? <span style={{ color: "var(--danger)" }}> *</span> : null}</label>;
    // Built-in rows carry a pvKey so their sandbox value can't collide with a
    // template field that happens to share a label ("Title", "Date", …) — the
    // two render side by side on a default template.
    const K = f.pvKey || f.label;
    const V = pv[K];
    if (f.type === "TextArea") return <div className="pvf">{lab}<textarea className="pvctl" placeholder={f.placeholder} value={V || ""} onChange={(e) => setPvVal(K, e.target.value)} /></div>;
    if (f.type === "Number") return <div className="pvf">{lab}<input className="pvctl" value={V || ""} placeholder="0" onChange={(e) => setPvVal(K, e.target.value)} /></div>;
    if (["Dropdown", "Facility"].includes(f.type)) {
      const os = f.type === "Facility" ? ["Sunrise Group Home", "Maple House"] : f.options;
      return <div className="pvf">{lab}<select className="pvctl" value={V || ""} onChange={(e) => setPvVal(K, e.target.value)}><option value="">Select…</option>{os.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
    }
    if (f.type === "SingleChoice") return <div className="pvf">{lab}<div className="pvseg">{(f.options || []).map((o) => <button key={o} aria-pressed={V === o} onClick={() => setPvVal(K, o)}>{o}</button>)}</div></div>;
    if (f.type === "Severity") return <div className="pvf">{lab}<div className="pvseg">{(f.severityOptions || []).map((o) => <button key={o.label} aria-pressed={V === o.label} onClick={() => setPvVal(K, o.label)}><span style={{ width: 8, height: 8, borderRadius: "50%", background: o.color, display: "inline-block", marginRight: 6 }} />{o.label}</button>)}</div></div>;
    if (f.type === "YesNo") return <div className="pvf">{lab}<div className="pvseg"><button aria-pressed={V !== "Yes"} onClick={() => setPvVal(K, "No")}>{f.noLabel || "No"}</button><button aria-pressed={V === "Yes"} onClick={() => setPvVal(K, "Yes")}>{f.yesLabel || "Yes"}</button></div></div>;
    if (f.type === "MultipleChoice") return <div className="pvf">{lab}<div className="pvseg">{(f.options || []).map((o) => { const on = Array.isArray(V) && V.includes(o); return <button key={o} aria-pressed={on} onClick={() => setPvVal(K, on ? V.filter((x) => x !== o) : [...(V || []), o])}>{o}</button>; })}</div></div>;
    if (f.type === "Calculation") return <div className="pvf">{lab}<div className="pvcalc">{evalCalc(f)}</div></div>;
    if (f.type === "Notifications") return <div className="pvf">{lab}<div className="pvctl" style={{ color: "var(--muted)", fontSize: 12.5 }}>Notifies {(f.notifyRoles || []).join(", ") || "no one yet"}.</div></div>;
    if (["Client", "Staff"].includes(f.type)) return <div className="pvf">{lab}<div className="pvctl" style={{ color: "var(--muted)" }}>{f.type === "Staff" ? "Selected from staff" : "Selected from clients"}</div></div>;
    if (f.type === "CarePlan") return <div className="pvf">{lab}<div className="pvctl" style={{ color: "var(--muted)" }}>Attached from the client's active care plan</div></div>;
    if (f.type === "Date") return <div className="pvf">{lab}<input className="pvctl" value={V || ""} placeholder="Select date" onChange={(e) => setPvVal(K, e.target.value)} /></div>;
    if (f.type === "Time") return <div className="pvf">{lab}<input className="pvctl" value={V || ""} placeholder="--:-- --" onChange={(e) => setPvVal(K, e.target.value)} /></div>;
    // Built-in only — there's no Photos field type in the palette, so this
    // exists purely to stand in for the drawer's upload dropzone.
    if (f.type === "Photos") return <div className="pvf">{lab}<div className="pvctl" style={{ color: "var(--muted)" }}>Drag images here or click to upload</div></div>;
    return <div className="pvf">{lab}<input className="pvctl" value={V || ""} placeholder={f.placeholder} onChange={(e) => setPvVal(K, e.target.value)} /></div>;
  };
  // A request type only replaces the drawer's built-in form with its own fields
  // when it HAS fields and isn't a global default — this is templateDefinesForm
  // in ServiceRequestNewDrawer, and the preview has to apply the same rule to be
  // a preview at all. Without it, viewing a default template showed a form made
  // only of that template's own fields, silently omitting the Client Name(s) /
  // Staff Name(s) / Title / Priority / Issue Category / Description / photo rows
  // that are the actual form staff fill in for those types.
  const ownFieldCount = M.sections.reduce((n, s) => n + s.fields.length, 0);
  const templateDefinesForm = !template?.isDefault && ownFieldCount > 0;
  // The built-in Facility row drops out when the type collects Facility itself,
  // so the same question is never asked twice — same rule as the drawer. Only
  // counts a Facility field that's actually reachable in the preview right
  // now: one sitting in a section or behind a field-level condition that
  // isn't met (same checks the section/field rendering below applies) would
  // otherwise suppress this fallback while never itself appearing.
  const hasOwnFacility = M.sections.some((s) => sectionCondMet(s) && s.fields.some((f) => f.type === "Facility" && condOn(f.conditional)));
  // Facility has no template-level fallback (same reasoning as the drawer's
  // own templateDefinesForm/templateFacilityFieldId split): a type that
  // defines its own form still gets a standalone Facility row when it hasn't
  // collected Facility itself, instead of dropping the question entirely.
  const builtInCards = templateDefinesForm
    ? (hasOwnFacility ? [] : [
        {
          id: "__bi_details",
          title: "Details",
          fields: [{ id: "__bi_facility", pvKey: "__bi_facility", label: "Facility", type: "Facility", span: 6 }],
        },
      ])
    : [
        {
          id: "__bi_details",
          title: "Details",
          fields: [
            { id: "__bi_staff", pvKey: "__bi_staff", label: "Staff Name(s)", type: "Staff", span: 12 },
            { id: "__bi_clients", pvKey: "__bi_clients", label: "Client Name(s)", type: "Client", span: 12 },
            ...(hasOwnFacility ? [] : [{ id: "__bi_facility", pvKey: "__bi_facility", label: "Facility", type: "Facility", span: 6 }]),
            { id: "__bi_shift", pvKey: "__bi_shift", label: "Shift", type: "Dropdown", options: SHIFTS, span: 6 },
            { id: "__bi_date", pvKey: "__bi_date", label: "Date", type: "Date", span: 6 },
            { id: "__bi_time", pvKey: "__bi_time", label: "Time", type: "Time", span: 6 },
          ],
        },
        {
          id: "__bi_request",
          title: "Request",
          fields: [
            { id: "__bi_title", pvKey: "__bi_title", label: "Title", type: "Text", required: true, placeholder: "Short summary…", span: 12 },
            { id: "__bi_priority", pvKey: "__bi_priority", label: "Priority", type: "Dropdown", options: PRIORITY_LEVELS, required: true, span: 6 },
            { id: "__bi_cat", pvKey: "__bi_cat", label: "Issue Category", type: "Dropdown", options: previewCategoryNames, required: true, span: 6 },
            { id: "__bi_desc", pvKey: "__bi_desc", label: "Description", type: "TextArea", required: true, placeholder: "Describe the request…", span: 12 },
            { id: "__bi_photos", pvKey: "__bi_photos", label: "Upload Image", type: "Photos", span: 12 },
          ],
        },
      ];

  const renderPreview = () => (
    <div className="pane" style={{ gridColumn: "1 / -1" }}>
      <div className="pvstage">
        <div className="drawer">
          {/* The template's own name, since that IS the request type staff pick
              — a generic "New Service Request" gave no clue which type you were
              looking at, which matters most on the default templates where the
              form below is identical between them. Falls back while a new
              template is still unnamed. */}
          <div className="dhead">{M.name?.trim() || "New Service Request"}</div>
          <div className="dbody">
            {builtInCards.map((card) => (
              <div className="pvcard" key={card.id}>
                <div className="ch">{card.title}</div>
                <div className="pvgrid">
                  {card.fields.map((f) => <div key={f.id} style={{ gridColumn: `span ${f.span}` }}>{pvField(f)}</div>)}
                </div>
              </div>
            ))}
            {M.sections
              .filter(sectionCondMet)
              // An empty section is scaffolding on the canvas but noise in the
              // preview once the built-in form is what's really being shown —
              // the drawer drops empty cards outright (templateFormCards).
              .filter((sec) => templateDefinesForm || sec.fields.length > 0)
              .map((sec) => {
                const vis = sec.fields.filter((f) => condOn(f.conditional));
                return (
                  <div className="pvcard" key={sec.id}>
                    <div className="ch">{sec.title}</div>
                    <div className="pvgrid">
                      {!vis.length ? <div style={{ gridColumn: "span 12", color: "var(--muted2)", fontSize: 12.5 }}>No visible fields.</div> : null}
                      {vis.map((f) => <div key={f.id} style={{ gridColumn: `span ${f.span}` }}>{pvField(f)}</div>)}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );

  /* ── settings step: a 3-part wizard (Approval, Statuses, Access) mirroring the
     Request Template Studio reference. Board/Priority/Active/SubmitRoles all
     persist via templatePayload(). notifyAdmins/statusList have no backend
     model support today, so those two stay builder-only session state for
     visual parity, same rationale as sections/conditional/calc above.
     Approvers and the required-signature count are NOT edited here — per the
     mockup, that lives in a separate "Approvals" editor opened from this
     template row in the Templates list (ServiceRequestSettings.jsx's
     ApprovalsModal), gated behind its own Edit action. This step only covers
     whether approval is required at all and who gets notified of new
     requests. ── */

  const renderApprovalStep = () => {
    const s = M.settings;
    return (
      <div className="setgroup">
        <div className="setg-h">Approval &amp; Notifications</div>
        <div className="set-switch">
          <div><div className="sl">Require approval</div></div>
          <input type="checkbox" className="tog" checked={s.approvalRequired}
            onChange={(e) => { s.approvalRequired = e.target.checked; rerender(); }} />
        </div>
        <div className="set-sub">Who gets notified</div>
        <div className="adminlist">
          {admins.map((a, i) => {
            const id = a.id || a._id;
            const notif = s.notifyAdmins.includes(id);
            const isOwner = String(id) === String(currentUserId);
            const roleLabel = isOwner ? "Owner" : (a.role || a.type);
            return (
              <div className="adminrow2" key={id}>
                <span className="avpic" style={{ background: AV_COLORS[i % AV_COLORS.length], fontSize: 11, fontWeight: 600 }}>
                  {initialsOf(nameOf(a))}
                </span>
                <span className="aninline">{nameOf(a)} {roleLabel ? <span className="ar">({roleLabel})</span> : null}</span>
                <span className="rowctrls">
                  <button className={`belltog ${notif ? "on" : ""}`} title="Receives request notifications"
                    onClick={() => { const idx = s.notifyAdmins.indexOf(id); idx >= 0 ? s.notifyAdmins.splice(idx, 1) : s.notifyAdmins.push(id); rerender(); }}>
                    <Svg n="notif" w={15} s={2} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const STATUS_COLOR = { backlog: "#98A2B3", ready: "#0BA5EC", in_review: "#F79009", approved: "#12B76A", resolved: "#2E90FA", rejected: "#F04438" };
  const renderStatusesStep = () => {
    const s = M.settings;
    const enabledCount = s.statusList.filter((x) => x.enabled !== false).length;
    const addStatus = () => {
      const v = newStatusName.trim();
      if (!v) return;
      const id = "ts" + s.statusList.length + "_" + v.length;
      s.statusList.push({ id, label: v, system: false, enabled: true });
      setNewStatusName("");
      rerender();
    };
    const removeStatus = (st) => {
      s.statusList = s.statusList.filter((x) => x.id !== st.id);
      rerender();
    };
    const toggleStatus = (st) => {
      const on = st.enabled !== false;
      if (on && enabledCount <= 1) return; // keep at least one status available
      st.enabled = !on;
      rerender();
    };
    return (
      <div className="setgroup">
        <div className="setg-h">Statuses</div>
        <div className="set-sub" style={{ marginTop: 0 }}>Toggle which statuses are available on this template's board</div>
        <div className="statuslist">
          {s.statusList.map((st) => {
            const selected = st.enabled !== false;
            return (
              <div className={`statusrow ${selected ? "" : "off"}`} key={st.id}
                onClick={(e) => { if (e.target.closest("[data-statusdel]")) return; toggleStatus(st); }}>
                <span className="statusdot" style={{ background: STATUS_COLOR[st.id] || "#7A5AF8" }} />
                <span className="statusname">{st.label}</span>
                <span className={`statustag ${st.system ? "sys" : "custom"}`}>{st.system ? "System" : "Custom"}</span>
                {st.system ? null : (
                  <button className="statusdel2" data-statusdel title="Remove status"
                    onClick={(e) => { e.stopPropagation(); removeStatus(st); }}>✕</button>
                )}
                <span className={`stog ${selected ? "on" : ""}`} />
              </div>
            );
          })}
        </div>
        <div className="addstatus">
          <input placeholder="Add a custom status…" maxLength={24} value={newStatusName}
            onChange={(e) => setNewStatusName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addStatus(); }} />
          <button className="addstatusbtn" onClick={addStatus}>+ Add Status</button>
        </div>
      </div>
    );
  };

  const renderAccessStep = () => {
    const s = M.settings;
    const roleRow = (label, key) => {
      const on = s.submitRoles[key];
      return (
        <div className="adminrow" key={key} onClick={() => { s.submitRoles[key] = !s.submitRoles[key]; rerender(); }}>
          <span className={`abox ${on ? "on" : ""}`}>{on ? <Svg n="check" w={12} s={3} /> : null}</span>
          <span className="an" style={{ flex: 1, fontWeight: 600 }}>{label}</span>
        </div>
      );
    };
    return (
      <div className="setgroup">
        <div className="setg-h">Who Can Submit</div>
        <div className="set-switch" style={{ borderTop: "none" }}>
          <div><div className="sl">Available to all staff</div></div>
          <input type="checkbox" className="tog" checked={s.allStaff}
            onChange={(e) => { s.allStaff = e.target.checked; rerender(); }} />
        </div>
        {!s.allStaff ? (
          <>
            <div className="set-sub">Limit to roles</div>
            <div className="adminlist">
              {roleRow("Staff", "staff")}
              {roleRow("Admin-role staff", "admin")}
              {roleRow("Contractors", "contractor")}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const SETTINGS_STEPS = [
    { title: "Approval", render: renderApprovalStep },
    { title: "Statuses", render: renderStatusesStep },
    { title: "Access", render: renderAccessStep },
  ];

  const renderSettings = () => {
    const cur = Math.min(Math.max(settingsWizStep, 0), SETTINGS_STEPS.length - 1);
    return (
      <div className="stepwrap">
        <div className="stepinner">
          <div className="step-title">Template settings</div>
          <div className="wizsteps">
            {SETTINGS_STEPS.map((st, i) => {
              const state = i === cur ? "active" : i < cur ? "done" : "";
              return (
                <span key={st.title} style={{ display: "contents" }}>
                  <button className={`wizstep ${state}`} onClick={() => setSettingsWizStep(i)}>
                    <span className="wiznum">{i < cur ? <Svg n="check" w={13} s={3} /> : i + 1}</span>
                    <span className="wizlbl">{st.title}</span>
                  </button>
                  {i < SETTINGS_STEPS.length - 1 ? <span className="wizline" /> : null}
                </span>
              );
            })}
          </div>
          <div className="wizbody">{SETTINGS_STEPS[cur].render()}</div>
          <div className="wiznav">
            <button className="wizbtn ghost" disabled={cur === 0} onClick={() => setSettingsWizStep(cur - 1)}>Back</button>
            <div style={{ flex: 1 }} />
            {cur < SETTINGS_STEPS.length - 1 ? (
              <button className="wizbtn primary" onClick={() => setSettingsWizStep(cur + 1)}>Next</button>
            ) : (
              <button className="wizbtn primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Template"}</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sr-studio">
      <style>{STUDIO_CSS}</style>

      <div className="bar">
        <button className="backbtn" onClick={back}><Svg n="chev" w={15} /> Back</button>
        <div className="spacer" />
        {step === "build" && !viewOnly ? (
          <div className="seg">
            <button aria-pressed={mode === "build"} onClick={() => setMode("build")}><Svg n="careplan" w={14} /> Build</button>
            <button aria-pressed={mode === "preview"} onClick={() => setMode("preview")}><Svg n="eye" w={14} /> Preview</button>
          </div>
        ) : null}
        <div className="spacer" />
        {step === "build" ? (
          <button className="ghost" onClick={() => setSchemaOpen(true)}>{"</>"} Schema</button>
        ) : null}
        {viewOnly ? (
          <span className="viewonly"><Svg n="eye" w={14} /> View only</span>
        ) : (
          <button className="primary" disabled={saving} onClick={() => (step === "settings" ? save() : goSettings())}>
            {step === "settings" ? (saving ? "Saving…" : "Save Template") : "Save & Next"}
          </button>
        )}
      </div>

      {step === "settings" ? renderSettings() : (
        <div className={`work ${mode === "preview" ? "preview" : ""}`}>
          {mode === "preview" ? renderPreview() : (<>{renderPalette()}{renderCanvas()}{renderProps()}</>)}
        </div>
      )}

      <div className={`schema ${schemaOpen ? "open" : ""}`}>
        <div className="sh">
          <Svg n="code" w={15} /> Generated template definition
          <button onClick={() => setSchemaOpen(false)}>✕</button>
        </div>
        {schemaOpen ? <pre dangerouslySetInnerHTML={{ __html: jsonHi(templatePayload(M)) }} /> : <pre />}
      </div>

      {toast ? <div className="toast"><Svg n="check" w={17} /> {toast}</div> : null}
    </div>
  );
};

export default ServiceRequestTemplateBuilder;
